import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, Wrench, ChevronDown, Minimize2, Maximize2, Copy, Check, GripVertical, Download, ExternalLink, FileText } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  timestamp: Date;
  toolCall?: {
    name: string;
    arguments: Record<string, any>;
  };
  toolResult?: {
    name: string;
    result: any;
    success: boolean;
  };
  isStreaming?: boolean;
}

interface ChatWidgetProps {
  caseId?: string;
  position?: 'bottom-right' | 'bottom-left';
  defaultOpen?: boolean;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({
  caseId: propCaseId,
  position = 'bottom-right',
  defaultOpen = false,
}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Get token from localStorage (same as other components)
  const token = localStorage.getItem('auth_token');

  // Extract caseId from URL if not provided as prop
  // Supports routes like /cases/:caseId or /case/:caseId
  const caseId = useMemo(() => {
    if (propCaseId) return propCaseId;
    const match = location.pathname.match(/\/cases?\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : undefined;
  }, [propCaseId, location.pathname]);

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showTools, setShowTools] = useState(false);
  const [availableTools, setAvailableTools] = useState<Array<{ name: string; description: string }>>([]);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [collapsedTools, setCollapsedTools] = useState<Set<string>>(new Set());
  const [chatHeight, setChatHeight] = useState(600);
  const [chatWidth, setChatWidth] = useState(384); // 96 * 4 = 384px (w-96)
  const [isResizing, setIsResizing] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<{ startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Copy message to clipboard
  const copyMessage = useCallback(async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, []);

  // Toggle tool collapse
  const toggleToolCollapse = useCallback((messageId: string) => {
    setCollapsedTools((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  // Format timestamp
  const formatTime = useCallback((date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }, []);

  // Render tool result with special handling for download URLs
  const renderToolResult = useCallback((result: any, toolName: string) => {
    // Check if result has a download URL
    if (result?.downloadUrl || result?.success) {
      const hasDownload = result.downloadUrl;
      const fullDownloadUrl = hasDownload ? (result.downloadUrl.startsWith('/') ? `${API_BASE}${result.downloadUrl}` : result.downloadUrl) : null;

      return (
        <div className="space-y-2">
          {/* Success/status message */}
          {result.message && (
            <div className="flex items-center gap-2 text-green-700 dark:text-green-300 font-medium">
              <Check className="w-4 h-4" />
              <span>{result.message}</span>
            </div>
          )}

          {/* Download button for export tools */}
          {fullDownloadUrl && (
            <a
              href={fullDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
            >
              <Download className="w-4 h-4" />
              Download {result.fileName || result.format?.toUpperCase() || 'File'}
            </a>
          )}

          {/* Show other relevant fields */}
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            {result.format && <div>Format: <span className="font-mono">{result.format.toUpperCase()}</span></div>}
            {result.wordCount && <div>Word count: {result.wordCount}</div>}
            {result.sectionKey && <div>Section: <span className="font-mono">{result.sectionKey}</span></div>}
            {result.fileName && !fullDownloadUrl && <div>File: {result.fileName}</div>}
          </div>
        </div>
      );
    }

    // For petition/letter content viewing tools
    if (toolName === 'get_petition_content' && result?.sections) {
      return (
        <div className="space-y-2">
          <div className="font-medium text-amber-900 dark:text-amber-100">
            {result.petitionType || 'Petition'} - {result.totalSections} sections
          </div>
          <div className="space-y-1">
            {result.sections.slice(0, 5).map((section: any) => (
              <div key={section.key} className="flex items-center gap-2 text-xs">
                <FileText className="w-3 h-3 text-amber-700 dark:text-amber-400" />
                <span className="font-mono">{section.key}</span>
                <span className="text-gray-500">({section.wordCount} words)</span>
              </div>
            ))}
            {result.sections.length > 5 && (
              <div className="text-gray-500 text-xs">...and {result.sections.length - 5} more sections</div>
            )}
          </div>
        </div>
      );
    }

    // Default: show JSON for other results
    return (
      <pre className="text-xs bg-amber-100 dark:bg-amber-950/50 p-2 rounded-lg overflow-x-auto text-amber-900 dark:text-amber-100 max-h-40">
        {JSON.stringify(result, null, 2)}
      </pre>
    );
  }, [API_BASE]);

  // Resize handlers
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: chatWidth,
      startHeight: chatHeight,
    };
  }, [chatWidth, chatHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;

      const deltaX = resizeRef.current.startX - e.clientX;
      const deltaY = resizeRef.current.startY - e.clientY;

      const newWidth = Math.max(320, Math.min(800, resizeRef.current.startWidth + deltaX));
      const newHeight = Math.max(400, Math.min(window.innerHeight * 0.8, resizeRef.current.startHeight + deltaY));

      setChatWidth(newWidth);
      setChatHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to close
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
      // Cmd/Ctrl + Enter to send
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        sendMessage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Initialize chat session
  const initSession = useCallback(async () => {
    if (!token) {
      setConnectionError('Not authenticated. Please log in to use chat.');
      return;
    }

    setIsInitializing(true);
    setConnectionError(null);

    try {
      const response = await fetch(`${API_BASE}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ caseId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Session creation failed:', response.status, errorText);
        throw new Error(`Failed to create session (${response.status})`);
      }

      const data = await response.json();
      if (data.success) {
        setSessionId(data.data.sessionId);
        setAvailableTools(
          data.data.availableTools?.map((name: string) => ({ name, description: '' })) || []
        );
        setConnectionError(null);

        // Add welcome message
        setMessages([
          {
            id: 'welcome',
            role: 'assistant',
            content: `Hello! I'm your AI assistant for immigration case management. ${
              caseId
                ? "I can help you with this case's documents, criteria analysis, letters, and more."
                : 'Select a case to get started, or ask me general questions about visa requirements.'
            }\n\nWhat would you like to know?`,
            timestamp: new Date(),
          },
        ]);
      } else {
        throw new Error(data.message || 'Failed to initialize chat session');
      }
    } catch (error) {
      console.error('Failed to init chat session:', error);
      setConnectionError(
        error instanceof Error
          ? error.message
          : 'Failed to connect to AI assistant. Please try again.'
      );
      // Show error in messages area as well
      setMessages([
        {
          id: 'error',
          role: 'assistant',
          content: 'I encountered an error connecting to the server. Please try closing and reopening the chat, or refresh the page.',
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsInitializing(false);
    }
  }, [token, caseId, API_BASE]);

  // Fetch available tools
  const fetchTools = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE}/api/chat/tools`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setAvailableTools(data.data.tools);
      }
    } catch (error) {
      console.error('Failed to fetch tools:', error);
    }
  }, [token, API_BASE]);

  // Reset session when caseId changes
  useEffect(() => {
    if (caseId) {
      setSessionId(null);
      setMessages([]);
    }
  }, [caseId]);

  // Initialize when chat opens
  useEffect(() => {
    if (isOpen && !sessionId) {
      initSession();
      fetchTools();
    }
  }, [isOpen, sessionId, initSession, fetchTools]);

  // Send message with streaming
  const sendMessage = async () => {
    if (!inputValue.trim() || !sessionId || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Add placeholder for assistant response
    const assistantMessageId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      },
    ]);

    try {
      const response = await fetch(`${API_BASE}/api/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: inputValue, stream: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);

              if (parsed.type === 'text' && parsed.content) {
                accumulatedContent += parsed.content;
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessageId
                      ? { ...m, content: accumulatedContent }
                      : m
                  )
                );
              } else if (parsed.type === 'tool_call') {
                setMessages((prev) => [
                  ...prev,
                  {
                    id: `tool-call-${Date.now()}`,
                    role: 'tool',
                    content: `Using tool: ${parsed.toolCall.name}`,
                    timestamp: new Date(),
                    toolCall: parsed.toolCall,
                  },
                ]);
              } else if (parsed.type === 'tool_result') {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.toolCall?.name === parsed.toolResult.name
                      ? { ...m, toolResult: parsed.toolResult }
                      : m
                  )
                );
              } else if (parsed.type === 'done') {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessageId
                      ? { ...m, isStreaming: false }
                      : m
                  )
                );
              } else if (parsed.type === 'error') {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessageId
                      ? { ...m, content: `Error: ${parsed.error}`, isStreaming: false }
                      : m
                  )
                );
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId
            ? {
                ...m,
                content: 'Sorry, I encountered an error. Please try again.',
                isStreaming: false,
              }
            : m
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const positionClasses = position === 'bottom-right' ? 'right-4' : 'left-4';

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-4 ${positionClasses} z-50 p-4 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 group border-2 border-blue-500`}
        aria-label="Open chat"
      >
        <MessageCircle className="w-7 h-7 group-hover:rotate-12 transition-transform duration-300" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
      </button>
    );
  }

  return (
    <div
      ref={chatRef}
      style={{
        width: isMinimized ? '20rem' : `${chatWidth}px`,
        height: isMinimized ? '3.5rem' : `${Math.min(chatHeight, window.innerHeight * 0.8)}px`,
      }}
      className={`fixed bottom-4 ${positionClasses} z-50 flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-2xl border-2 border-gray-300 dark:border-gray-600 transition-all duration-200 ${
        isResizing ? 'select-none' : ''
      }`}
    >
      {/* Resize Handle */}
      {!isMinimized && (
        <div
          onMouseDown={startResize}
          className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize hover:bg-blue-500/20 rounded-tl-lg flex items-center justify-center group z-10"
          title="Drag to resize"
        >
          <GripVertical className="w-3 h-3 text-gray-400 group-hover:text-blue-500 rotate-45" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg shadow-md">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <span className="font-semibold">AI Assistant</span>
          {caseId && (
            <span className="text-xs bg-blue-500/80 px-2 py-0.5 rounded-full backdrop-blur-sm" title={`Case: ${caseId}`}>
              Case Active
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowTools(!showTools)}
            className="p-1.5 hover:bg-blue-500/50 rounded-lg transition-colors"
            title="View tools"
          >
            <Wrench className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-blue-500/50 rounded-lg transition-colors"
            title={isMinimized ? 'Maximize' : 'Minimize'}
          >
            {isMinimized ? (
              <Maximize2 className="w-4 h-4" />
            ) : (
              <Minimize2 className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-red-500/50 rounded-lg transition-colors"
            title="Close (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Tools Panel */}
          {showTools && (
            <div className="p-4 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-850 border-b border-gray-300 dark:border-gray-600 text-sm max-h-40 overflow-y-auto">
              <div className="font-semibold mb-3 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Available Tools ({availableTools.length})
              </div>
              <div className="grid grid-cols-2 gap-2">
                {availableTools.map((tool) => (
                  <div
                    key={tool.name}
                    className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5 bg-white dark:bg-gray-700 px-2 py-1.5 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600"
                  >
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span className="font-mono font-medium">{tool.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Connection Error Banner */}
          {connectionError && (
            <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{connectionError}</span>
                <button
                  onClick={() => {
                    setSessionId(null);
                    setConnectionError(null);
                    setMessages([]);
                    initSession();
                  }}
                  className="ml-auto text-xs px-2 py-1 bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700 rounded transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50 scroll-smooth">
            {/* Initialization Loading State */}
            {isInitializing && (
              <div className="flex justify-center py-8">
                <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Connecting to AI assistant...</span>
                </div>
              </div>
            )}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                } group`}
              >
                {message.role === 'tool' ? (
                  // Collapsible Tool Call Card
                  <div className="w-full max-w-[90%]">
                    <button
                      onClick={() => toggleToolCollapse(message.id)}
                      className="w-full bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-3 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                          <span className="font-mono font-semibold text-sm text-amber-900 dark:text-amber-100">
                            {message.toolCall?.name || 'Tool'}
                          </span>
                          {message.toolResult && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                message.toolResult.success
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              }`}
                            >
                              {message.toolResult.success ? 'Success' : 'Failed'}
                            </span>
                          )}
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-amber-700 dark:text-amber-400 transition-transform duration-200 ${
                            collapsedTools.has(message.id) ? '-rotate-90' : ''
                          }`}
                        />
                      </div>
                      {!collapsedTools.has(message.id) && message.toolCall && (
                        <div className="mt-3 text-left">
                          <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
                            Arguments:
                          </div>
                          <pre className="text-xs bg-amber-100 dark:bg-amber-950/50 p-2 rounded-lg overflow-x-auto text-amber-900 dark:text-amber-100">
                            {JSON.stringify(message.toolCall.arguments, null, 2)}
                          </pre>
                          {message.toolResult && (
                            <>
                              <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1 mt-2">
                                Result:
                              </div>
                              <div className="text-xs">
                                {renderToolResult(message.toolResult.result, message.toolCall?.name || '')}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </button>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 px-2">
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                ) : (
                  // Regular Message Bubble
                  <div className="flex flex-col max-w-[85%]">
                    <div className="flex items-start gap-2">
                      {message.role === 'assistant' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-md">
                          <Bot className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div
                          className={`rounded-2xl px-4 py-3 shadow-md relative ${
                            message.role === 'user'
                              ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-tr-sm'
                              : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-2 border-gray-200 dark:border-gray-700 rounded-tl-sm'
                          }`}
                        >
                          <div
                            className={`text-sm prose prose-sm max-w-none ${
                              message.role === 'user'
                                ? 'prose-invert [&_a]:text-blue-200 [&_code]:text-blue-100'
                                : 'dark:prose-invert [&_pre]:bg-gray-900 [&_pre]:border [&_pre]:border-gray-700 [&_code]:bg-gray-100 dark:[&_code]:bg-gray-900 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm'
                            } [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1`}
                          >
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                code(props) {
                                  const { node, inline, className, children, ...rest } = props as any;
                                  const match = /language-(\w+)/.exec(className || '');
                                  return !inline ? (
                                    <div className="relative group/code">
                                      <div className="flex items-center justify-between bg-gray-800 dark:bg-gray-950 px-3 py-1 rounded-t-lg border-b border-gray-700">
                                        <span className="text-xs text-gray-400 font-mono">
                                          {match ? match[1] : 'code'}
                                        </span>
                                        <button
                                          onClick={() => copyMessage(`${message.id}-code`, String(children))}
                                          className="opacity-0 group-hover/code:opacity-100 transition-opacity p-1 hover:bg-gray-700 rounded"
                                          title="Copy code"
                                        >
                                          {copiedMessageId === `${message.id}-code` ? (
                                            <Check className="w-3 h-3 text-green-400" />
                                          ) : (
                                            <Copy className="w-3 h-3 text-gray-400" />
                                          )}
                                        </button>
                                      </div>
                                      <pre className="!mt-0 !rounded-t-none overflow-x-auto">
                                        <code className={className} {...rest}>
                                          {children}
                                        </code>
                                      </pre>
                                    </div>
                                  ) : (
                                    <code className={className} {...rest}>
                                      {children}
                                    </code>
                                  );
                                },
                              }}
                            >
                              {message.content}
                            </ReactMarkdown>
                            {message.isStreaming && (
                              <span className="inline-flex items-center gap-1 ml-2">
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => copyMessage(message.id, message.content)}
                            className={`absolute top-2 right-2 p-1.5 rounded-lg transition-opacity ${
                              message.role === 'user'
                                ? 'bg-blue-700/50 hover:bg-blue-700 text-white'
                                : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                            } opacity-0 group-hover:opacity-100`}
                            title="Copy message"
                          >
                            {copiedMessageId === message.id ? (
                              <Check className="w-3.5 h-3.5" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                        <div className={`text-xs text-gray-500 dark:text-gray-400 mt-1 px-2 ${
                          message.role === 'user' ? 'text-right' : 'text-left'
                        }`}>
                          {formatTime(message.timestamp)}
                        </div>
                      </div>
                      {message.role === 'user' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center shadow-md">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {isLoading && !messages.some(m => m.isStreaming) && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 shadow-md">
                  <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t-2 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={isInitializing ? "Connecting..." : connectionError ? "Connection failed - click Retry above" : "Type your message..."}
                  disabled={isLoading || !sessionId || isInitializing || !!connectionError}
                  rows={1}
                  className="w-full px-4 py-3 text-sm border-2 border-gray-300 dark:border-gray-600 rounded-xl
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    dark:bg-gray-800 dark:text-white
                    disabled:opacity-50 disabled:cursor-not-allowed
                    placeholder:text-gray-400 dark:placeholder:text-gray-500
                    resize-none transition-all duration-200
                    shadow-sm focus:shadow-md"
                  style={{
                    minHeight: '44px',
                    maxHeight: '120px',
                  }}
                />
              </div>
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim() || !sessionId || isInitializing || !!connectionError}
                className="flex-shrink-0 p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl
                  hover:from-blue-700 hover:to-blue-800
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-blue-700
                  transition-all duration-200 shadow-md hover:shadow-lg
                  transform hover:scale-105 active:scale-95"
                title="Send message (Enter)"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <div className="text-gray-500 dark:text-gray-400">
                AI can make mistakes. Verify important information.
              </div>
              <div className="flex items-center gap-3 text-gray-400 dark:text-gray-500">
                <span className="hidden sm:inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs font-mono">
                    Enter
                  </kbd>
                  <span>send</span>
                </span>
                <span className="hidden sm:inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs font-mono">
                    {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
                  </kbd>
                  <span>quick send</span>
                </span>
                <span className="hidden sm:inline-flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs font-mono">
                    Esc
                  </kbd>
                  <span>close</span>
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWidget;
